// src/contexts/authClient.js
// Centralize all in-memory auth state and axios instance

import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── In-memory auth state (jamais localStorage) ───────────────────────────────
export const memoryAuth = {
  csrfToken: null,
  role: null,
  user: null,
  refreshTimer: null,

  set(data) {
    this.csrfToken = data.csrf_token;
    this.role = data.role;
    this.user = data.user;
  },

  clear() {
    this.csrfToken = null;
    this.role = null;
    this.user = null;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  },

  isAuthenticated() {
    return !!this.csrfToken;
  },
};

// ─── Intercepteur CSRF partagé ────────────────────────────────────────────────
const csrfInterceptor = (config) => {
  const mutating = ['post', 'put', 'patch', 'delete'];
  if (mutating.includes(config.method) && memoryAuth.csrfToken) {
    config.headers['X-CSRF-Token'] = memoryAuth.csrfToken;
  }
  return config;
};

// ─── Instance axios pour le backend (avec credentials) ────────────────────────
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});
api.interceptors.request.use(csrfInterceptor);

// ─── Instance axios pour les API externes (SANS credentials) ──────────────────
export const externalApi = axios.create({
  // Pas de withCredentials
});

// ─── Configuration GLOBALE : Garder withCredentials pour tous les appels axios ─
// Ceci est NÉCESSAIRE pour que tous les composants existants continuent à fonctionner
axios.defaults.withCredentials = true;
axios.defaults.baseURL = API_BASE;
axios.interceptors.request.use(csrfInterceptor);

// ─── Auto-refresh ─────────────────────────────────────────────────────────────
const REFRESH_BEFORE_MS = 90 * 1000; // rafraîchir 90s avant expiration

function _getAccessTokenExpiry() {
  // Lire l'expiry réel depuis le cookie csrf_token (non-HttpOnly, même durée que access_token)
  // On ne peut pas lire le JWT access_token (HttpOnly), donc on utilise une valeur de fallback
  // basée sur la variable d'env ou 30min par défaut
  const ttlMin = parseInt(process.env.REACT_APP_ACCESS_TTL_MIN) || 30;
  return ttlMin * 60 * 1000;
}

export function scheduleTokenRefresh(onLogout) {
  // Annuler tout timer existant
  if (memoryAuth.refreshTimer) {
    clearTimeout(memoryAuth.refreshTimer);
    memoryAuth.refreshTimer = null;
  }

  const ttlMs = _getAccessTokenExpiry();
  const delay = Math.max(ttlMs - REFRESH_BEFORE_MS, 30 * 1000); // minimum 30s

  memoryAuth.refreshTimer = setTimeout(async () => {
    try {
      const res = await api.post('/auth/refresh');
      memoryAuth.csrfToken = res.data.csrf_token;
      scheduleTokenRefresh(onLogout); // replanifier après refresh réussi
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Session expirée côté serveur → logout propre
        memoryAuth.clear();
        if (onLogout) onLogout();
      } else {
        // Erreur réseau temporaire → réessayer dans 30s
        memoryAuth.refreshTimer = setTimeout(() => scheduleTokenRefresh(onLogout), 30 * 1000);
      }
    }
  }, delay);
}

// ─── Logout ───────────────────────────────────────────────────────────────────
export async function logout(navigate) {
  try {
    await api.post('/auth/logout');
  } catch {
    // best-effort
  }
  memoryAuth.clear();
  navigate('/login', { replace: true });
}